// Snooze and Archive at the top of a card, so a decision does not wait for the footer: the same
// two local actions the footer shows as quiet buttons, on the same keys (h, a). Snooze offers the
// picker's presets in a menu; "Pick a time…" opens the full picker in the footer. A card snoozed
// once before (U5) opens the footer picker instead, so the smaller step is offered first.
import { Button } from "@web/ui/Button";
import { Menu, type MenuItem } from "@web/ui/Menu";
import type { CardView } from "@shared/api";
import { footerActions } from "../actions";
import { SNOOZE_PRESETS } from "./SnoozePicker";
import type { CardController } from "./useCardController";

function snoozeItems(controller: CardController): MenuItem[] {
  return [
    ...SNOOZE_PRESETS.map((p) => ({
      label: p.label,
      disabled: controller.busy,
      onSelect: () => void controller.snooze(p.until, p.label),
    })),
    {
      label: "Pick a time…",
      disabled: controller.busy,
      onSelect: () => {
        controller.disarm();
        controller.setSnoozeOpen(true);
      },
    },
  ];
}

export function QuickDecisions({
  card,
  controller,
}: {
  card: CardView;
  controller: CardController;
}) {
  const { quiet } = footerActions(card.actions);
  if (!quiet.length || !controller.actionable) return null;
  return (
    <div
      role="group"
      aria-label="Quick decisions"
      data-testid="quick-decisions"
      className="ml-auto flex items-center gap-1"
    >
      {quiet.map(({ action, key }) =>
        action.behavior === "snooze" && !card.smallerStep ? (
          <Menu
            key={action.id}
            label={action.label}
            variant="ghost"
            items={snoozeItems(controller)}
          />
        ) : (
          <Button
            key={action.id}
            size="sm"
            variant="ghost"
            disabled={controller.busy}
            title={key ? `${action.label} (${key})` : undefined}
            aria-keyshortcuts={key ?? undefined}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => controller.press(action)}
          >
            {action.label}
          </Button>
        ),
      )}
    </div>
  );
}
